import { Controller } from '@hotwired/stimulus'
import { DirectUpload } from "@rails/activestorage"
import Dropzone from "dropzone"
import pica from 'pica'
import { getMetaValue, findElement, removeElement, insertAfter } from "../helpers"

Dropzone.autoDiscover = false

export default class extends Controller {
  static targets = ["input", "preview", "label"]
  static values = {
    url: String,
    pendingBlob: String,
    pendingBlobUrl: String
  }

  connect() {
    this.uploadsInProgress = 0
    this.disconnected = false
    this.dropZone = this.createDropZone()
    this.restorePendingBlob() || this.defaultValue()
    this.bindEvents()
    this.inputTarget.addEventListener('click', (e) => e.preventDefault())
  }

  disconnect() {
    this.disconnected = true
    // Cancel all pending uploads and timeouts on Turbo navigation
    this.dropZone.files.forEach(file => {
      if (file.uploadTimeout) {
        clearTimeout(file.uploadTimeout)
      }
      if (file.controller?.xhr) {
        file.controller.xhr.abort()
      }
    })
    this.dropZone.destroy()
  }

  // Restore a pending blob that failed validation (e.g., file too large)
  // This allows the user to see what they uploaded and preserves the form state
  restorePendingBlob() {
    if (!this.hasPendingBlobValue || !this.pendingBlobValue) {
      return false
    }

    // Create hidden input with the pending blob's signed_id
    const hiddenInput = this.createHiddenInputForPendingBlob(this.pendingBlobValue)

    // Show preview if URL is available
    if (this.hasPendingBlobUrlValue && this.pendingBlobUrlValue) {
      const mockFile = { name: "pending", size: 0, isMock: true }
      // Store reference to hidden input so it can be removed when file is removed
      mockFile.controller = { hiddenInput }
      this.dropZone.emit("addedfile", mockFile)
      this.dropZone.emit("thumbnail", mockFile, this.pendingBlobUrlValue)
      this.labelTarget.classList.add('sr-only')
      this.inputTarget.removeAttribute('required')
    }

    return true
  }

  createHiddenInputForPendingBlob(signedId) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.inputTarget.name
    input.value = signedId
    insertAfter(input, this.inputTarget)
    return input
  }

  defaultValue() {
    if (this.inputTarget.dataset.defaultValue) {
      const mockFile = { name: "default", size: 0, isMock: true }
      this.dropZone.emit("addedfile", mockFile)
      this.dropZone.emit("thumbnail", mockFile, this.inputTarget.dataset.defaultValue)
      this.labelTarget.classList.add('sr-only')
      this.inputTarget.removeAttribute('required')
    } else if (this.inputTarget.dataset.required == "true") {
      this.inputTarget.setAttribute('required', 'required')
    }
  }

  createDropZone() {
    return new Dropzone(this.element, {
      url: this.url,
      previewTemplate: this.previewTarget.innerHTML,
      headers: this.headers,
      maxFiles: this.maxFiles,
      maxFilesize: this.maxFileSize,
      acceptedFiles: this.acceptedFiles,
      clickable: this.labelTarget,
      autoQueue: false
    })
  }

  bindEvents() {
    this.dropZone.on("addedfile", (file) => {
      // Skip upload machinery for pre-existing files (pending blobs, default values)
      // These already have their hidden input and preview set up
      if (file.isMock) {
        this.labelTarget.classList.add('sr-only')
        this.inputTarget.removeAttribute('required')
        return
      }

      // Disable submit button immediately when file is added
      this.uploadStarted()
      file.uploadPending = true  // Track that uploadFinished() is needed

      file.uploadTimeout = setTimeout(() => {
        if (file.accepted) {
          createDirectUploadController(this, file).start()
        } else {
          // Re-enable button if file was rejected
          this.uploadFinished()
          file.uploadPending = false
        }
      }, 500)

      this.labelTarget.classList.add('sr-only')

      const list = new DataTransfer()
      list.items.add(file)

      this.inputTarget.files = list.files

      this.inputTarget.removeAttribute('required')
    })

    this.dropZone.on("removedfile", (file) => {
      // Always remove the hidden input for the old file
      file.controller && removeElement(file.controller.hiddenInput)

      // Only update UI if user explicitly removed file (clicked X),
      // not during file replacement via maxfilesexceeded
      if (!this.replacingFiles) {
        // Clear any pending upload timeout to prevent ghost uploads
        if (file.uploadTimeout) {
          clearTimeout(file.uploadTimeout)
          file.uploadTimeout = null
        }
        // Cancel in-progress upload to prevent orphaned hidden input
        if (file.uploadPending) {
          file.canceled = true
          if (file.controller?.xhr) {
            file.controller.xhr.abort()
          }
          this.uploadFinished()
          file.uploadPending = false
        }
        this.inputTarget.value = ''
        this.labelTarget.classList.remove('sr-only')

        if (this.inputTarget.dataset.required) {
          this.inputTarget.setAttribute('required', 'required')
        }
      }
    })

    this.dropZone.on("maxfilesexceeded", (file) => {
      // Remove old files while preserving UI state
      this.replacingFiles = true
      this.dropZone.files.slice(0, -1).forEach(f => {
        // Clear any pending upload timeout for the old file
        if (f.uploadTimeout) {
          clearTimeout(f.uploadTimeout)
          f.uploadTimeout = null
        }
        // Mark as canceled to prevent callbacks from double-processing
        f.canceled = true
        // Abort in-flight upload (callback will bail due to canceled flag)
        if (f.controller?.xhr) {
          f.controller.xhr.abort()
        }
        // Balance uploadStarted() for the canceled file
        if (f.uploadPending) {
          this.uploadFinished()
          f.uploadPending = false
        }
        this.dropZone.removeFile(f)
      })
      this.replacingFiles = false

      // Clear pending timeout since file.accepted is already false
      // (Dropzone's accept() runs synchronously before this handler)
      if (file.uploadTimeout) {
        clearTimeout(file.uploadTimeout)
        file.uploadTimeout = null
      }

      // Manually accept and start upload for the replacement file
      file.accepted = true
      createDirectUploadController(this, file).start()
    })

    this.dropZone.on("canceled", (file) => {
      // Clear pending upload timeout
      if (file.uploadTimeout) {
        clearTimeout(file.uploadTimeout)
        file.uploadTimeout = null
      }
      // Abort XHR if upload is in progress
      if (file.controller?.xhr) {
        file.controller.xhr.abort()
      }
      // Clean up hidden input if created
      if (file.controller?.hiddenInput) {
        removeElement(file.controller.hiddenInput)
      }
      // Balance upload counter
      if (file.uploadPending) {
        this.uploadFinished()
        file.uploadPending = false
      }
    })
  }

  get headers() { return { "X-CSRF-Token": getMetaValue("csrf-token") } }

  get url() { return this.inputTarget.getAttribute("data-direct-upload-url") }

  get maxFiles() { return parseInt(this.data.get("maxFiles"), 10) || 1 }

  get maxFileSize() { return parseInt(this.data.get("maxFileSize"), 10) || 256 }

  get acceptedFiles() { return this.data.get("acceptedFiles") }

  get form() { return this.element.closest("form") }

  get submitButton() { return this.form && findElement(this.form, "input[type=submit], button[type=submit]") }

  // Track uploads to prevent form submission during upload
  uploadStarted() {
    this.uploadsInProgress++
    this.updateSubmitButton()
  }

  uploadFinished() {
    this.uploadsInProgress = Math.max(0, this.uploadsInProgress - 1)
    this.updateSubmitButton()
  }

  updateSubmitButton() {
    const submitButton = this.submitButton
    if (submitButton) {
      submitButton.disabled = this.uploadsInProgress > 0
    }
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.source = source
    this.file = file
  }

  start() {
    this.resizeImage(this.file)
      .then(resizedFile => {
        // Bail if controller disconnected during resize or file was canceled
        if (this.source.disconnected) return
        if (this.file.canceled) return

        this.directUpload = createDirectUpload(resizedFile, this.source.url, this)
        this.file.controller = this
        this.directUpload.create((error, attributes) => {
          // Bail if controller disconnected during upload or file was canceled
          if (this.source.disconnected) return
          if (this.file.canceled) return

          if (error) {
            this.source.uploadFinished()
            this.file.uploadPending = false
            this.emitDropzoneError(error)
          } else {
            // Create hidden input ONLY after successful upload, with value set immediately
            // This prevents race condition where user submits before upload completes
            this.hiddenInput = this.createHiddenInput(attributes.signed_id)
            // Clear file input so only the hidden input (with signed_id) is submitted
            this.source.inputTarget.value = ''
            this.source.uploadFinished()
            this.file.uploadPending = false
            this.emitDropzoneSuccess()
          }
        })
      })
      .catch(error => {
        // Bail if controller disconnected or file was canceled
        if (this.source.disconnected) return
        if (this.file.canceled) return

        this.source.uploadFinished()
        this.file.uploadPending = false
        this.emitDropzoneError(error)
      })
  }

  resizeImage(file) {
    // Pass through non-images unchanged (PDFs, docs, etc.)
    if (!file.type.startsWith('image/')) {
      return Promise.resolve(file)
    }
    // GIFs don't resize well with canvas, pass through
    if (file.type === 'image/gif') {
      return Promise.resolve(file)
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (readerEvent) => {
        const image = new Image()
        image.onerror = () => reject(new Error('Failed to load image'))
        image.onload = () => {
          const canvas = document.createElement('canvas')
          const maxSize = 800 // Example: maximum width or height
          let { width, height } = image

          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height
              height = maxSize
            }
          }

          canvas.width = width
          canvas.height = height

          pica().resize(image, canvas)
            .then(resizedCanvas => pica().toBlob(resizedCanvas, file.type, 0.90))
            .then(blob => {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              })
              resolve(resizedFile)
            })
            .catch(reject)
        }
        image.src = readerEvent.target.result
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // Private
  createHiddenInput(value) {
    // Remove any existing hidden inputs with the same name to prevent duplicates
    const existingInputs = this.source.element.querySelectorAll(`input[type="hidden"][name="${this.source.inputTarget.name}"]`)
    existingInputs.forEach(input => removeElement(input))

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.source.inputTarget.name
    input.value = value // Set value immediately - never have an empty hidden input
    insertAfter(input, this.source.inputTarget)
    return input
  }

  directUploadWillStoreFileWithXHR(xhr) {
    this.bindProgressEvent(xhr)
    this.emitDropzoneUploading()
  }

  bindProgressEvent(xhr) {
    this.xhr = xhr
    this.xhr.upload.addEventListener("progress", event => this.uploadRequestDidProgress(event))
  }

  uploadRequestDidProgress(event) {
    const progressBar = findElement(this.file.previewTemplate, ".dz-upload")
    if (!progressBar) return

    const progress = event.loaded / event.total * 100
    if (progress < 100) {
      progressBar.style.width = `${progress}%`
    } else {
      progressBar.classList.add('hidden')
    }
  }

  emitDropzoneUploading() {
    this.file.status = Dropzone.UPLOADING
    this.source.dropZone.emit("processing", this.file)
  }

  emitDropzoneError(error) {
    this.file.status = Dropzone.ERROR
    this.source.dropZone.emit("error", this.file, error)
    this.source.dropZone.emit("complete", this.file)
  }

  emitDropzoneSuccess() {
    this.file.status = Dropzone.SUCCESS
    this.source.dropZone.emit("success", this.file)
    this.source.dropZone.emit("complete", this.file)
  }
}

// Top level...
function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file)
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller)
}
